"use client";

import { t } from "@/lib/content";
import type { Lang, LocalizedString } from "@/lib/types";

export type ContactPillOption = {
  value: string;
  label: LocalizedString;
};

type CcContactPillFieldProps = {
  lang: Lang;
  id: string;
  label: LocalizedString;
  options: ContactPillOption[];
  mode: "single" | "multiple";
  value: string | string[];
  onChange: (value: string | string[]) => void;
  invalid?: boolean;
};

function isSelected(
  mode: "single" | "multiple",
  value: string | string[],
  optionValue: string,
): boolean {
  if (mode === "single") {
    return value === optionValue;
  }
  return Array.isArray(value) && value.includes(optionValue);
}

export function CcContactPillField({
  lang,
  id,
  label,
  options,
  mode,
  value,
  onChange,
  invalid = false,
}: CcContactPillFieldProps) {
  const labelId = `${id}-label`;

  function handleSingleSelect(optionValue: string) {
    onChange(optionValue);
  }

  function handleMultipleToggle(optionValue: string) {
    const current = Array.isArray(value) ? value : [];
    if (current.includes(optionValue)) {
      onChange(current.filter((v) => v !== optionValue));
    } else {
      onChange([...current, optionValue]);
    }
  }

  return (
    <div
      className={`cc-contact__cell cc-contact__cell--pills cc-contact__cell--${id}${invalid ? " cc-contact__cell--invalid" : ""}`}
    >
      <span className="cc-contact__label" id={labelId}>
        {t(label, lang)}
      </span>
      {mode === "single" ? (
        <div
          className="cc-contact__pills"
          role="radiogroup"
          aria-labelledby={labelId}
        >
          {options.map((option) => {
            const selected = isSelected(mode, value, option.value);
            const inputId = `${id}-${option.value}`;
            return (
              <label
                key={option.value}
                className={`cc-contact__pill${selected ? " cc-contact__pill--selected" : ""}`}
              >
                <input
                  id={inputId}
                  className="cc-contact__pill-input"
                  type="radio"
                  name={id}
                  value={option.value}
                  checked={selected}
                  onChange={() => handleSingleSelect(option.value)}
                />
                <span className="cc-contact__pill-text">
                  {t(option.label, lang)}
                </span>
              </label>
            );
          })}
        </div>
      ) : (
        <div
          className="cc-contact__pills"
          role="group"
          aria-labelledby={labelId}
        >
          {options.map((option) => {
            const selected = isSelected(mode, value, option.value);
            const inputId = `${id}-${option.value}`;
            return (
              <label
                key={option.value}
                className={`cc-contact__pill${selected ? " cc-contact__pill--selected" : ""}`}
              >
                <input
                  id={inputId}
                  className="cc-contact__pill-input"
                  type="checkbox"
                  name={id}
                  value={option.value}
                  checked={selected}
                  onChange={() => handleMultipleToggle(option.value)}
                />
                <span className="cc-contact__pill-text">
                  {t(option.label, lang)}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
